/**
 * Economy, round-major (BENCH-V2-SPEC §11). Round 0 submits the controls as one batch; round r (1 to 3, a fourth when a
 * re-cut held an attempt) submits one request per pending chunk; each round's results go through the same checks as Standard
 * inside the bench worker (`BenchRun.applyRound`). Every batch id is in the job record before the driver waits, so a reload
 * resumes by polling the stored ids; a round's answers that arrived are kept until applied. Anything the round does not cover
 * (the dialogue judge, a truncation or schema retry, a contributor rework) is a real-time call through the run's `complete`
 * and `judge`, at the standard price.
 *
 * A budget stop is checked between rounds, never inside one: a submitted batch always completes.
 */
import { costOfUsage } from '../../engine/bench/cost.ts';
import type { ProviderAnswer, RunReport, Usage } from '../../engine/bench/loop2.ts';
import type { Effort } from '../../shared/models.ts';
import { chunkCompletionRequest } from '../rewrite/adapters.ts';
import type { BatchRequest, BatchResult, BatchStatus } from '../rewrite/provider-batch.ts';
import { batchCustomId } from '../rewrite/provider-batch.ts';
import type { BenchClient } from './bench-client.ts';
import type { BatchRecord, BenchJob } from './job.ts';

/** Rounds: three attempts, and one more for a unit whose attempt was held for a re-cut. */
export const MAX_ROUNDS = 4;

export interface BatchApi {
	submit(requests: readonly BatchRequest[]): Promise<string[]>;
	/** `status` carries the Batch API's own request counts when the caller has them (a time estimate's basis, UD-11). */
	collect(ids: readonly string[]): Promise<{
		done: boolean;
		status?: readonly BatchStatus[];
		results: BatchResult[];
	}>;
	cancel(ids: readonly string[]): Promise<void>;
}

export interface EconomyDeps {
	client: Pick<BenchClient, 'controls' | 'calibrate' | 'round' | 'apply' | 'finish'>;
	batch: BatchApi;
	/** Persists the record (after every submit and every applied round). */
	save(job: BenchJob): Promise<void>;
	model: string;
	effort: Effort;
	signal?: AbortSignal;
	pollMs?: number;
	sleep?: (ms: number) => Promise<void>;
	/** The budget: true when the spent amount is over the limit (checked between rounds). */
	over(): boolean;
	/** Adds a round's cost to the budget. */
	spend(usd: number): void;
	onProgress?(info: EconomyProgress): void;
}

/** What the driver reports (UD-11): which step of a round it is in and, while waiting, the batches' own counts. */
export interface EconomyProgress {
	round: number;
	pending: number;
	waiting: boolean;
	phase?: 'submitting' | 'waiting' | 'applying';
	/** While waiting: requests the batches have finished (any outcome) of the total. Absent when the status has no counts. */
	answered?: number;
	total?: number;
}

export type EconomyEnd =
	| { kind: 'done'; report: RunReport; job: BenchJob }
	| { kind: 'paused'; job: BenchJob }
	| { kind: 'cancelled'; job: BenchJob };

const CHUNK_KEY = /^c(\d+)-(\d+)(?:p(\d+))?v(\d+)$/;

/** The custom id of a request: a chunk key (`c3-5v0`) or `control:<n>`. */
export function customIdOf(key: string, round: number): string {
	const control = /^control:(\d+)$/.exec(key);
	if (control) {
		const n = Number(control[1]);
		return batchCustomId({ a: n, b: n, v: 0, round, purpose: 'control' });
	}
	const m = CHUNK_KEY.exec(key);
	if (!m) throw new RangeError(`not a chunk key: ${key}`);
	return batchCustomId({
		a: Number(m[1]),
		b: Number(m[2]),
		...(m[3] !== undefined ? { piece: Number(m[3]) } : {}),
		v: Number(m[4]),
		round,
	});
}

function answerOf(r: BatchResult): ProviderAnswer | undefined {
	if (r.status !== 'succeeded' || !r.response) return undefined;
	return {
		parsed: r.response.parsed ?? r.response.text,
		stopReason: r.response.stopReason,
		...(r.response.usage ? { usage: r.response.usage } : {}),
	};
}

const addUsage = (u: Usage, a: ProviderAnswer): Usage => ({
	requests: u.requests + 1,
	inputTokens: u.inputTokens + (a.usage?.inputTokens ?? 0),
	outputTokens: u.outputTokens + (a.usage?.outputTokens ?? 0),
	cacheReadTokens: u.cacheReadTokens + (a.usage?.cacheReadTokens ?? 0),
	cacheWriteTokens: u.cacheWriteTokens + (a.usage?.cacheWriteTokens ?? 0),
});

export async function runEconomy(start: BenchJob, deps: EconomyDeps): Promise<EconomyEnd> {
	let job: BenchJob = start;
	const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
	const persist = async (patch: Partial<BenchJob>): Promise<void> => {
		job = { ...job, ...patch, updatedAt: new Date().toISOString() };
		await deps.save(job);
	};

	/** Polls until the batch ended; `null` when cancelled. Keeps the answers so a reload before `apply` loses nothing. */
	const wait = async (rec: BatchRecord): Promise<Array<[string, ProviderAnswer]> | null> => {
		if (job.collected) return job.collected;
		for (;;) {
			if (deps.signal?.aborted) return null;
			let got: Awaited<ReturnType<BatchApi['collect']>>;
			try {
				got = await deps.batch.collect(rec.batchIds);
			} catch (e) {
				// a poll aborted by the cancel is a cancel, not a failure
				if (deps.signal?.aborted) return null;
				throw e;
			}
			if (got.done) {
				const answers: Array<[string, ProviderAnswer]> = [];
				let usage = job.usage;
				let usd = 0;
				for (const r of got.results) {
					const key = rec.keys[r.customId];
					const answer = answerOf(r);
					if (key === undefined || !answer) continue;
					answers.push([key, answer]);
					usage = addUsage(usage, answer);
					usd += costOfUsage(
						job.model || deps.model,
						{
							inputTokens: answer.usage?.inputTokens ?? 0,
							outputTokens: answer.usage?.outputTokens ?? 0,
							cacheReadTokens: answer.usage?.cacheReadTokens ?? 0,
							cacheWriteTokens: answer.usage?.cacheWriteTokens ?? 0,
						},
						{ economy: true }
					);
				}
				deps.spend(usd);
				await persist({
					collected: answers,
					usage,
					costUsd: job.costUsd + usd,
					spentUsd: job.spentUsd + usd,
				});
				return answers;
			}
			const counts = (got.status ?? []).reduce(
				(a, b) => {
					const c = b.counts;
					const total = c.processing + c.succeeded + c.errored + c.canceled + c.expired;
					return { total: a.total + total, answered: a.answered + total - c.processing };
				},
				{ total: 0, answered: 0 }
			);
			deps.onProgress?.({
				round: rec.round,
				pending: got.results.length,
				waiting: true,
				phase: 'waiting',
				...(counts.total > 0 ? counts : {}),
			});
			await sleep(deps.pollMs ?? 5000);
		}
	};

	const submit = async (
		round: number,
		items: ReadonlyArray<{ key: string; request: Parameters<typeof chunkCompletionRequest>[0] }>
	): Promise<BatchRecord> => {
		const keys: Record<string, string> = {};
		const requests: BatchRequest[] = items.map((it) => {
			const customId = customIdOf(it.key, round);
			keys[customId] = it.key;
			return {
				customId,
				request: chunkCompletionRequest(it.request, { model: deps.model, effort: deps.effort }),
			};
		});
		deps.onProgress?.({ round, pending: requests.length, waiting: false, phase: 'submitting' });
		const batchIds = await deps.batch.submit(requests);
		const rec: BatchRecord = { round, batchIds, keys };
		await persist({ batches: [...job.batches, rec] });
		return rec;
	};

	const markApplied = async (rec: BatchRecord, patch: Partial<BenchJob>): Promise<void> => {
		const { collected: _drop, ...rest } = job;
		job = rest;
		await persist({
			...patch,
			batches: job.batches.map((b) =>
				b === rec || b.round === rec.round ? { ...b, applied: true } : b
			),
		});
	};

	const cancelled = async (): Promise<EconomyEnd> => {
		const open = job.batches.filter((b) => !b.applied).flatMap((b) => b.batchIds);
		if (open.length > 0) await deps.batch.cancel(open).catch(() => undefined);
		await persist({ status: 'cancelled' });
		return { kind: 'cancelled', job };
	};

	// round 0: the controls (floors), unless the job is past them
	if ((job.phase ?? 'controls') === 'controls') {
		let rec = job.batches.find((b) => b.round === 0 && !b.applied);
		if (!rec) {
			const requests = await deps.client.controls();
			if (requests.length === 0) {
				const r = await deps.client.calibrate([]);
				await persist({ snapshotJson: r.snapshotJson, phase: 'rounds', round: 0 });
			} else {
				rec = await submit(
					0,
					requests.map((request, n) => ({ key: `control:${n}`, request }))
				);
			}
		}
		if (rec) {
			const answers = await wait(rec);
			if (answers === null) return cancelled();
			deps.onProgress?.({ round: 0, pending: 0, waiting: false, phase: 'applying' });
			const r = await deps.client.calibrate(
				answers.map(([k, a]) => [Number(k.slice('control:'.length)), a] as [number, ProviderAnswer])
			);
			await markApplied(rec, { snapshotJson: r.snapshotJson, phase: 'rounds', round: 0 });
		}
	}

	// rounds
	for (;;) {
		const round = job.round ?? 0;
		let rec = job.batches.find((b) => b.round === round + 1 && !b.applied);
		if (!rec) {
			if (round >= MAX_ROUNDS) break;
			if (deps.over()) {
				await persist({ status: 'paused' });
				return { kind: 'paused', job };
			}
			const requests = await deps.client.round();
			if (requests.length === 0) break;
			rec = await submit(
				round + 1,
				requests.map((q) => ({ key: q.key, request: q.request }))
			);
		}
		const answers = await wait(rec);
		if (answers === null) return cancelled();
		deps.onProgress?.({ round: round + 1, pending: 0, waiting: false, phase: 'applying' });
		const r = await deps.client.apply(answers);
		await markApplied(rec, {
			snapshotJson: r.snapshotJson,
			round: round + 1,
			doneChunks: r.done,
		});
	}

	const report = await deps.client.finish();
	await persist({ status: 'done', report, resultText: report.text, coverage: report.coverage });
	const { snapshotJson: _s, ...done } = job;
	job = done;
	await deps.save(job);
	return { kind: 'done', report, job };
}
